<template>
  <b-icon-text class="b--select" :class="buttonClasses" :title="title" @click="onClick">
    <!-- Selected icon -->
    <div
      slot="icon"
      class="b__tick-container"
      :class="tickable ? 'b__tick-container--tickable' : ''"
      @click="onTickClick"
    >
      <span class="b__tick">
        <svg-tick v-show="!showCross" class="svg-fill" />
        <svg-close v-show="showCross" class="svg-fill" />
      </span>
    </div>
    <!-- Selected children count -->
    <span v-show="subCount" slot="label" class="b__count">{{ subCount }}</span>
  </b-icon-text>
</template>

<script>
import IconTextButton from './iconTextButton'
import SvgClose from '../../assets/svg/close.svg?inline'
import SvgTick from '../../assets/svg/tick.svg?inline'

export default {
  components: {
    'b-icon-text': IconTextButton,
    'svg-close': SvgClose,
    'svg-tick': SvgTick,
  },
  computed: {
    /**
     * Select button classes
     */
    buttonClasses() {
      let classes = this.selected ? 'b--selected' : ''
      if (this.showCross) {
        classes += `${classes.length ? ' ' : ''}b--cross`
      }
      return classes
    },
  },
  methods: {
    /**
     * Emit click event to parent
     */
    onClick() {
      this.$emit('click')
    },

    /**
     * Emit tick click event
     */
    onTickClick(event) {
      if (this.tickable && event) {
        // Stop event propagation to button
        event.stopPropagation()

        // Emit event
        this.$emit('tick')
      }
    },
  },
  props: {
    /**
     * Whether button is selected
     */
    selected: {
      default: false,
      type: Boolean,
    },

    /**
     * Whether to show cross icon
     * instead of tick
     */
    showCross: {
      default: false,
      type: Boolean,
    },

    /**
     * Number of children selected
     */
    subCount: {
      default: 0,
      type: Number,
    },

    /**
     * Whether clicking tick icon
     * emits separate event
     */
    tickable: {
      default: false,
      type: Boolean,
    },

    /**
     * Button title
     */
    title: {
      required: true,
      type: String,
    },
  },
}
</script>

<style lang="postcss" scoped>
.b.b--select {
  @apply w-full h-auto text-grey-900;
  @apply px-3 py-2;
  min-height: 3rem;

  &:before {
    @apply absolute inset-0 rounded-md;
    @apply border border-solid border-transparent;
    @apply z-1 pointer-events-none;
    content: '';
  }

  /* Button title */
  & >>> .b__title {
    @apply text-left;
  }

  /* Selected icon */
  & .b__tick {
    @apply flex-shrink-0 relative;
    @apply w-b-sm h-b-sm;
    @apply rounded-full bg-grey-200;
  }
  & .b__tick svg {
    @apply relative w-full h-full;
    @apply mr-0 text-white z-1;
  }
  & .b__tick:before {
    @apply absolute inset-0 rounded-full;
    @apply bg-pink-disabled opacity-0;
    @apply z-0 pointer-events-none;
    content: '';
    transition: opacity 0.2s ease;
  }
  & .b__tick:after {
    @apply absolute inset-0 rounded-full;
    @apply border-2 border-solid border-transparent;
    @apply z-2 pointer-events-none;
    content: '';
  }

  /* Tickable selected icon */
  & .b__tick.b__tick-container--tickable:active:before {
    @apply opacity-75;
  }

  /* Tick icon select button */
  &:not(.b--cross) .b__tick {
    padding: 6px 5px 4px;
  }

  /* Cross icon select button */
  &.b--cross {
    @apply flex-row-reverse;
  }
  &.b--cross .b__tick {
    padding: 2px;
  }
  & .b__tick-container {
    @apply flex items-center justify-center;
    @apply mr-3;
  }
  & .b__tick-container--tickable {
    @apply mr-0;
    width: 48px;
    height: 48px;
  }
}

/* Selected children count */
.b__count {
  @apply flex justify-center items-center;
  @apply ml-2 pointer-events-none;
  @apply leading-none text-white-plain bg-red-500;
  min-width: 16px;
  height: 16px;
  border-radius: 8px;
  font-size: 10px;
  font-family: Arial;
}

/* Selected state */
.b.b--select.b--selected {
  /* Selected icon */
  & .b__tick {
    @apply bg-transparent;
  }
  & .b__tick svg {
    @apply text-red-500;
  }

  /* Tick icon */
  &:not(.b--cross) .b__tick:after {
    @apply border-red-500;
  }
}

@screen desktop {
  .b.b--select {
    min-height: 3.5rem;
  }
  /* Selected icon */
  .b.b--select .b__tick {
    @apply w-b-sm h-b-sm;
  }
  /* Tickable selected icon */
  .b.b--select .b__tick-container--tickable:hover .b__tick:before {
    @apply opacity-75;
  }

  /* Tick icon */
  .b.b--select:not(.b--cross) .b__tick {
    padding: 7px 6px 5px;
  }

  /* Selected children count */
  .b__count {
    @apply ml-3;
    min-width: 20px;
    height: 20px;
    border-radius: 10px;
    font-size: 12px;
  }
}
</style>
